import React, { useState, useRef, useEffect } from 'react';
import { WordExample } from '../utils/api';
import logger from '../utils/logger';

export interface WordItem {
  characters: string;
  pinyin: string;
  translation: string;
  priority?: number;
  example?: WordExample;
  originalData?: any; 
  isHighlighted?: boolean;
  isLearned?: boolean;
}

interface WordsListProps {
  words: WordItem[];
  className?: string;
  itemClassName?: string;
  title?: string;
  onWordClick?: (word: WordItem) => void;
  onWordDoubleClick?: (word: WordItem) => void;
  highlightedWords?: Set<string>;
  learnedIndicator?: boolean;
}

const WordsList: React.FC<WordsListProps> = ({
  words,
  className = 'results-list',
  itemClassName = 'result-item',
  title,
  onWordClick,
  onWordDoubleClick,
  highlightedWords = new Set(),
  learnedIndicator = false
}) => {
  const clickTimer = useRef<NodeJS.Timeout | null>(null);
  const clickCount = useRef(0);
  const [activeWord, setActiveWord] = useState<string | null>(null);
  
  const handleClick = (word: WordItem, e: React.MouseEvent) => {
    // Если не задан обработчик двойного клика, сразу вызываем onWordClick без задержки
    if (!onWordDoubleClick) {
      if (onWordClick) {
        onWordClick(word);
      }
      return;
    }
    
    // Логика с задержкой только если есть обработчик двойного клика
    clickCount.current += 1;
    
    if (clickCount.current === 1) {
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
      }
      
      // Устанавливаем таймер, чтобы отличить одиночный клик от двойного
      clickTimer.current = setTimeout(() => {
        // Если через 300мс не было второго клика, считаем клик одиночным
        if (clickCount.current === 1 && onWordClick) {
          onWordClick(word);
        }
        clickCount.current = 0;
        clickTimer.current = null;
      }, 300);
    } else if (clickCount.current === 2) {
      // Двойной клик
      clickCount.current = 0;
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
        clickTimer.current = null;
      }
      
      if (onWordDoubleClick) {
        onWordDoubleClick(word);
        // Сбрасываем активное слово после двойного клика
        setActiveWord(null);
      }
    }
  };
  
  useEffect(() => {
    logger.log('WordsList получил:', { words, className, itemClassName });
  }, [words, className, itemClassName]);
  
  return (
    <div className={className}>
      {title && (
        <div className="words-list-title">
          {title}
        </div>
      )}
      {words && words.length > 0 ? (
        words.map((word, index) => (
          <div 
            key={`${word.characters}-${index}`} 
            className={`${itemClassName} ${
              highlightedWords.has(word.characters) && word.characters !== activeWord ? 'highlighted' : ''
            } ${word.isLearned && learnedIndicator ? 'learned' : ''}`}
            onClick={(e) => handleClick(word, e)}
          >
            <div className="word">{word.characters}</div>
            <div className="pinyin">{word.pinyin}</div>
            <div className="translation">{word.translation}</div>
          </div>
        ))
      ) : (
        <div className="words-list-empty">
          Список слов пуст
        </div>
      )}
    </div>
  );
};

export default WordsList; 